import { type ActorRefFrom } from 'xstate';
import { z } from 'zod';
import { ERROR_MESSAGES } from '~/constants';
import { Provider } from '../../../types';
import { AddressFormValues, addressSchema, optionSchema, optionSchemaRequired } from '../../types';
import { Events } from './events';
import type { profileFormMachine } from './profileForm.machine';

const ssnRegex = new RegExp('^(?!666|000|9\\d{2})\\d{3}-(?!00)\\d{2}-(?!0{4})\\d{4}$');

export type CitizenType = 'United States' | 'Permanent Resident' | 'Visa' | 'Other';
export type GenderType = 'M' | 'F' | 'N' | 'O';
export type VisaStatusType = 'Active' | 'Pending' | 'Expiring' | 'Expiring Soon' | 'Expired';
export type VisaType = 'H-1B' | 'L-1' | 'O-1' | 'E-2' | 'TN' | 'Other';
export type PrefixType = 'Dr.' | 'Miss' | 'Mr.' | 'Mrs.' | 'Prof.' | 'Mx.' | '';
export type PhoneType = 'Mobile' | 'Fax' | 'Office' | 'Home';
export type AddressType = 'Primary' | 'Mailing' | 'Permanent' | 'Work';

export const emailSchema = z.object({
  emailAddress: z.string().email('Must be a valid email'),
  primaryFlag: z.boolean(),
});

export type EmailFormValues = z.infer<typeof emailSchema>;

export const phoneNumSchema = z.object({
  phoneType: optionSchema,
  primaryFlag: z.boolean(),
  phoneNum: z
    .string()
    .min(12, { message: 'Please enter a complete phone number' })
    .regex(/^\d{3}-\d{3}-\d{4}$/, 'Must be a valid phone number (###-###-####)'),
  countryCode: optionSchema,
});

export type PhoneFormValues = z.infer<typeof phoneNumSchema>;

export const PersonalFormSchema = z
  .object({
    firstName: z.string().min(1, { message: ERROR_MESSAGES.REQUIRED }),
    lastName: z.string().min(1, { message: ERROR_MESSAGES.REQUIRED }),
    middleName: z
      .string()
      .min(1, { message: ERROR_MESSAGES.REQUIRED })
      .or(z.literal(''))
      .optional(),
    prefixName: optionSchema,
    suffixName: z.string(),
    dateOfBirth: z.string().min(1, { message: ERROR_MESSAGES.REQUIRED }),
    gender: optionSchemaRequired,
    birthCountryCode: optionSchemaRequired,
    birthState: optionSchema,
    birthCity: z.string().min(1, { message: ERROR_MESSAGES.REQUIRED }),
    citizenshipStatus: optionSchemaRequired,
    visaStatus: optionSchema,
    visaType: optionSchema,
  })
  .superRefine((values, ctx) => {
    if (values.birthCountryCode.value === 'US' && !values.birthState.value) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: ERROR_MESSAGES.REQUIRED,
        path: ['birthState', 'value'],
      });
    }
  });

export const SensitiveInfoSchema = z.object({
  ssnEnc: z.string().regex(ssnRegex, 'Must be a valid SSN').or(z.literal('')).optional(),
  driversLicenseNumEnc: z
    .string()
    .min(4, { message: ERROR_MESSAGES.MIN_CHARACTER_LIMIT(4) })
    .max(20, { message: "Driver's license must be 20 characters or fewer" })
    .regex(/^[a-zA-Z0-9]*$/, 'Must contain only letters and numbers')
    .or(z.literal(''))
    .optional(),
  driversLicenseState: optionSchema,
  passportNumberEnc: z
    .string()
    .max(20, { message: 'Passport number must be 20 characters or fewer' })
    .regex(/^[a-zA-Z0-9]*$/, 'Must contain only letters and numbers')
    .or(z.literal(''))
    .optional(),
});

export const ProfessionalInfoSchema = z.object({
  npiNum: z
    .string()
    .length(10, 'NPI number must be exactly 10 digits')
    .regex(/^\d{10}$/, 'NPI must contain only numbers')
    .or(z.literal(''))
    .optional(),
  providerCategory: z
    .object({
      label: z.string(),
      value: z.number().min(1, { message: ERROR_MESSAGES.REQUIRED }),
    })
    .nullable()
    .refine((val): boolean => val !== null && val !== undefined, {
      message: ERROR_MESSAGES.REQUIRED,
      path: ['value'],
    }),
  providerType: z
    .object({
      label: z.string().min(1, { message: ERROR_MESSAGES.REQUIRED }),
      value: z.number().min(1, { message: ERROR_MESSAGES.REQUIRED }),
    })
    .nullable()
    .refine((val): boolean => val !== null && val !== undefined, {
      message: ERROR_MESSAGES.REQUIRED,
      path: ['value'],
    }),
  providerCategoryId: z.number(),
  providerTypeId: z.number(),
  languages: z.array(optionSchema),
});

export const ContactInformationFormSchema = z.object({
  emailAddresses: z.array(emailSchema),
  addresses: z.array(addressSchema),
  phoneNumbers: z.array(phoneNumSchema),
});

export type PersonalFormValues = z.infer<typeof PersonalFormSchema>;

export type SensitiveInfoValues = z.infer<typeof SensitiveInfoSchema>;

export type ProfessionalInfoValues = z.infer<typeof ProfessionalInfoSchema>;

export type ContactFormValues = z.infer<typeof ContactInformationFormSchema>;

export type ProfileFormValues = PersonalFormValues &
  SensitiveInfoValues &
  ProfessionalInfoValues &
  ContactFormValues;

export interface OptionNumType {
  label: string;
  value: number;
}

export interface OptionStringType {
  label: string;
  value: string;
  isFixed?: boolean;
}

export interface Context {
  addresses: AddressFormValues[];
  addressForm: Partial<AddressFormValues>;
  currentTab: number;
  submittingTab: number;
  editAddressIndex: number;
  editPhoneNumberIndex: number;
  editEmailAddressIndex: number;
  emailAddresses: EmailFormValues[];
  emailAddressForm: Partial<EmailFormValues>;
  errors: unknown;
  modal: { edit: boolean; create: boolean; type: 'address' | 'phone' | 'email' };
  personalInfo: PersonalFormValues;
  phoneNumberForm: Partial<PhoneFormValues>;
  phoneNumbers: PhoneFormValues[];
  professionalInfo: ProfessionalInfoValues;
  provider: Provider;
  sensitiveInfo: SensitiveInfoValues;
}

export interface Scope {
  context: Context;
  event: Events;
}

export type ProfileMachineRef = ActorRefFrom<typeof profileFormMachine>;
